import { redactStringValue, sanitizeRecord } from '@efficiently/models';

/**
 * Structural subset of a Sentry `Log` this scrubber touches — typed structurally so the
 * hook does not depend on an SDK type that moves between majors.
 */
export interface ScrubbableSentryLog {
	message?: unknown;
	attributes?: Record<string, unknown>;
}

/**
 * PII scrubbing hook for Sentry `beforeSendLog`.
 *
 * Logs bypass `beforeSend`, so `scrubSentryEvent` never sees them. The client logger's own
 * pipeline sanitises event metadata but not the message text, and any future SDK-side log
 * source is unfiltered entirely — this is the one place both are covered.
 *
 * The server twin is `scrubSentryLog` in `@efficiently/api-common`. Both run the same
 * redaction engine over the same two fields; the server one also masks JSON embedded in
 * strings, for the JSON its `consoleLoggingIntegration` inlines — the browser does not run
 * that integration. They are separate files because that one is deliberately kept out of
 * `@efficiently/models` (see its own doc comment) and this bundle cannot import a server package.
 *
 * Returns the same reference, mutated in place, to match the `beforeSendLog` contract.
 */
export function scrubSentryLog<TLog extends ScrubbableSentryLog>(log: TLog): TLog {
	const target = log as ScrubbableSentryLog;

	if (typeof target.message === 'string') {
		const redactedMessage = redactStringValue(target.message);

		if (redactedMessage !== target.message) {
			target.message = redactedMessage;
		}
	}

	if (target.attributes) {
		target.attributes = sanitizeRecord(target.attributes);
	}

	return log;
}
